// Debug-tools visibility — reveal the "Debug" nav category + home section when
// the per-user `debug.show_debug` setting is on.
//
// The nav header and home page are STATIC shared markup rendered before sign-in,
// so the Debug category ships hidden (`[hidden]`) and is unhidden here after the
// session resolves. Called once from the auth flow's post-sign-in hook
// (initAuthUI → showApp), so every page gets it with no per-page wiring.
//
// This is a convenience/visibility toggle, not a security boundary — per-user
// DynamoDB access is still fenced by the Identity Pool IAM policy. A hidden
// Debug page is still reachable by direct URL; the page itself re-checks the
// setting and shows a disabled notice when off.

import { buildDocClient, fetchIdentityId } from './aws.js';
import { getShowDebug } from '../model/settings.js';

/** Selector for every element that should appear only when Debug is enabled. */
const DEBUG_HOST_SELECTOR = '[data-debug-only]';

/** Unhide (or keep hidden) all Debug-only elements on the current page. */
export function applyDebugVisibility(show) {
  for (const el of document.querySelectorAll(DEBUG_HOST_SELECTOR)) {
    el.hidden = !show;
  }
}

/**
 * Resolve the user's `showDebug` setting and reveal Debug UI if enabled. Safe to
 * call on any page — if there are no Debug-only elements it's a no-op. Never
 * throws: a lookup failure just leaves the Debug UI hidden.
 *
 * @param session the `{ idToken, email }` session passed to the onSignedIn hook
 * @returns the resolved boolean (also useful to a Debug page's own guard)
 */
export async function revealDebugIfEnabled(session) {
  try {
    const idToken = session?.idToken;
    if (!idToken) return false;
    const [doc, userId] = await Promise.all([
      Promise.resolve(buildDocClient(idToken)),
      fetchIdentityId(idToken),
    ]);
    const show = await getShowDebug(doc, userId);
    applyDebugVisibility(show);
    return show;
  } catch {
    applyDebugVisibility(false);
    return false;
  }
}
